import { withCompInfo } from '@wix/editor-elements-integrations'
import type { ConvertedDisplayMode, SvgInfo, WPhotoCssVars, WPhotoDefinition, WPhotoSkin } from './WPhoto'
import type { ImageMaskCSSVars, Style, Image, Crop, ImageMask, StyleProperties } from '@wix/thunderbolt-becky-types'
import type { MaskFlip, ParsedSvg } from '@wix/thunderbolt-commons'
import { encodeSVG } from '@wix/thunderbolt-commons'
import type { CompDataVectorImage } from '../VectorImage/VectorImageHelpers'
import { getSvgStringForMask } from '../VectorImage/VectorImageHelpers'
import { getBoxDimensionsBySkin } from '@wix/thunderbolt-becky-root'
import type { DisplayModeProperty } from './WPhoto.schema'
import { pickBy } from 'lodash'
import { getHeightInPixels, getWidthInPixels } from '@wix/editor-elements-common-utils'

export type CompDataWPhoto = WPhotoDefinition['data'] &
	CompDataVectorImage & {
		originalImageDataRef: Image
		imageCrop: Crop
		imageMask: ImageMask
		imageMaskData: ImageMaskCSSVars
		overrideCrop: { [key: string]: string }
	}
export type WPhotoProperties = WPhotoDefinition['property'] & { displayMode: DisplayModeProperty }
export type WPhotoImage = Image & { focalPoint: { x: number; y: number } }
export type CatharsisImageMask = ImageMask & { svgContent?: string; svgId?: string; svgInfo?: SvgInfo }

type CropWithResolvedSvg = Crop & { resolvedSvg?: ParsedSvg }
type WPhotoPropertiesWithOverrides = WPhotoProperties & { overrideCrop?: CropWithResolvedSvg; resolvedSvg?: ParsedSvg }

const getBottomPadding = (skin: WPhotoSkin) => {
	if (skin === 'NewPolaroidPhoto') {
		return 50
	}
	if (skin === 'PolaroidPhoto') {
		return 30
	}
	return 0
}

const getConvertedDisplayMode = (displayMode: DisplayModeProperty): ConvertedDisplayMode =>
	displayMode === 'fitWidthStrict' ? 'fitWidth' : displayMode === 'fitHeightStrict' ? 'fitHeight' : displayMode

const getContainerSize = (
	width: number,
	height: number,
	image: Image,
	displayMode: ConvertedDisplayMode,
	skin: WPhotoSkin
) => {
	const paddingBottom = getBottomPadding(skin)
	const originalWidth = image.width
	const originalHeight = image.height
	const layoutWidth = width || 0
	const layoutHeight = height || 0
	const imageProportion = originalWidth / originalHeight
	const calculatedHeight = displayMode === 'fitWidth' ? layoutWidth / imageProportion + paddingBottom : layoutHeight
	const calculatedWidth = displayMode === 'fitHeight' ? layoutHeight * imageProportion : layoutWidth
	const roundHeight = `${Math.ceil(calculatedHeight)}px`
	const roundWidth = `${Math.ceil(calculatedWidth)}px`
	return {
		height: roundHeight,
		width: roundWidth,
		'--height': roundHeight,
		'--width': roundWidth,
	}
}

export const getImageMaskCSSVars = (maskData: CatharsisImageMask): ImageMaskCSSVars => {
	const attributes = {
		x: `${maskData.x}%`,
		y: `${maskData.y}%`,
		width: `${maskData.width}%`,
		height: `${maskData.height}%`,
	}

	return {
		'--mask-image': encodeSVG(maskData.svgContent || ''),
		'--mask-position': `${attributes.y} ${attributes.x}`,
		'--mask-size': `${attributes.width} ${attributes.height}`,
		'--mask-repeat': 'no-repeat',
	} as ImageMaskCSSVars
}

export const getMaskCSSVars = (
	svgId: string | undefined,
	resolvedSvg: ParsedSvg | undefined,
	flip: MaskFlip = 'none',
	overrideMask: any | undefined = null
): {
	maskCSSVars: ImageMaskCSSVars
	hasMask: boolean
} => {
	const maskData = overrideMask
		? overrideMask
		: {
			svgId,
			x: 0,
			y: 0,
			width: 100,
			height: 100,
			mode: 'alpha',
			flip,
			rotate: 0,
		}
	const hasMask = !!maskData?.svgId
	if (resolvedSvg) {
		maskData.svgContent = getSvgStringForMask({
			svgInfo: resolvedSvg.info,
			svgString: resolvedSvg.content,
			flip,
		})
		maskData.svgInfo = resolvedSvg.info
	}
	const maskCSSVars = hasMask ? getImageMaskCSSVars(maskData) : {}
	return {
		maskCSSVars: maskCSSVars as ImageMaskCSSVars,
		hasMask,
	}
}

const getMinImageDimensions = (width: number, height: number, skin: WPhotoSkin, styleProps: StyleProperties) => {
	const boxDimensions = getBoxDimensionsBySkin(skin, {
		style: {
			properties: styleProps,
		},
	} as Style)
	const boxWidth = boxDimensions.width
	const boxHeight = boxDimensions.height
	const minWidth = width - boxWidth <= 0 ? { '--image-min-width': '16px' } : {}
	const minHeight = height - boxHeight <= 0 ? { '--image-min-height': '16px' } : {}
	return Object.assign(minWidth, minHeight)
}

// Translate from carmi to regular ts
export const css = withCompInfo<WPhotoCssVars | {}, WPhotoDefinition>()(
	[
		'compData',
		'compProps',
		'skin',
		'compLayout',
		'compSingleLayout',
		'isMobileView',
		'styleProperties',
		'isOneDocMigrated',
	],
	({ compData, compProps, skin, compLayout, compSingleLayout, isMobileView, styleProperties, isOneDocMigrated }) => {
		const image = compData as CompDataWPhoto
		const imageProperties = compProps as WPhotoPropertiesWithOverrides
		const height = isOneDocMigrated ? getHeightInPixels(compSingleLayout) : compLayout.height
		const width = isOneDocMigrated ? getWidthInPixels(compSingleLayout) : compLayout.width

		const displayMode = getConvertedDisplayMode(imageProperties?.displayMode)
		const containerSize =
			displayMode === 'fitWidth' || displayMode === 'fitHeight'
				? getContainerSize(width!, height!, image, displayMode, skin)
				: { '--height': `${height}px`, '--width': `${width}px` }

		const focalPoint = image?.focalPoint?.x && image?.focalPoint
		const objectPosition = !isMobileView && focalPoint && `${focalPoint.x}% ${focalPoint.y}%`

		const fixed =
			!isOneDocMigrated && !!compLayout.fixedPosition && !compLayout.docked
				? {
					'--position-fixed': 'fixed',
					'--left': `${compLayout.x}px`,
					'--top': `${compLayout.y}px`,
					'--z-index': 50,
				}
				: {}

		const cropOverrides = imageProperties?.overrideCrop

		let cropData = {} as CropWithResolvedSvg | undefined
		if (cropOverrides !== null) {
			cropData = cropOverrides || image?.crop
		}

		const svgId = cropData?.svgId
		const cropFlip = cropData?.flip || ('none' as string)
		const resolvedSvg = image?.resolvedSvg || cropOverrides?.resolvedSvg || imageProperties?.resolvedSvg

		// we need to flip x and y since the original SVG impl. is flipped compared to the CSS one
		const flip = cropFlip === 'x' ? 'y' : cropFlip === 'y' ? 'x' : cropFlip === 'xy' ? 'xy' : 'none'
		const { maskCSSVars } = getMaskCSSVars(svgId, resolvedSvg, flip)

		return pickBy(
			{
				'--object-position': objectPosition,
				...fixed,
				...getMinImageDimensions(width || 0, height || 0, skin, styleProperties),
				...containerSize,
				...maskCSSVars,
			},
			(x) => x
		) as WPhotoCssVars
	}
)
